import React from 'react';
import LegalLayout from './LegalLayout';
import useSiteSettings from '../hooks/useSiteSettings';

const RefundPolicyPage = () => {
  const s = useSiteSettings();
  return (
  <LegalLayout title="Refund & Cancellation Policy" lastUpdated="February 03, 2026" testId="refund-policy-page">
    <p>
      This policy explains how cancellations and refunds work on <strong>BeeWeddy</strong>. It applies to package bookings,
      individual service bookings, and platform commission charges. By making or accepting a booking on BeeWeddy, you accept
      this policy.
    </p>

    <h2>1. Cancellation by the Customer</h2>

    <p>Cancellation windows are calculated from the <strong>event date</strong>, not the booking date.</p>

    <ul>
      <li><strong>More than 30 days before the event:</strong> Full refund of any advance paid <em>minus</em> a 5% platform fee.</li>
      <li><strong>15 – 30 days before the event:</strong> 50% refund of advance paid.</li>
      <li><strong>7 – 14 days before the event:</strong> 25% refund of advance paid.</li>
      <li><strong>Less than 7 days before the event:</strong> No refund (vendors have typically blocked the date and turned away other work).</li>
    </ul>

    <p>
      To cancel, go to <em>Dashboard → My Bookings → Cancel</em>, or contact <a href={`mailto:${s.support_email}`}>{s.support_email}</a>
      with your booking ID. Refunds are processed to the original payment method within <strong>7 working days</strong>.
    </p>

    <h2>2. Cancellation by the Vendor</h2>
    <p>If a vendor cancels a confirmed booking:</p>
    <ul>
      <li>The customer receives a <strong>100% refund</strong> of any amount paid, including platform fees.</li>
      <li>BeeWeddy will attempt to find a replacement vendor of equivalent tier; the customer may accept or take the refund.</li>
      <li>Vendors who repeatedly cancel may have their accounts suspended and forfeit pending wallet balance.</li>
    </ul>

    <h2>3. No-Show & Service Quality Issues</h2>
    <ul>
      <li><strong>Vendor no-show:</strong> If the assigned Captain marks the vendor / professional as absent, the customer is entitled to a <strong>full refund</strong> plus a service-credit equal to 10% of the booking value (max ₹2,000) for inconvenience.</li>
      <li><strong>Quality dispute:</strong> Customers must raise quality complaints within <strong>72 hours</strong> of the event with photo / video evidence. Our team mediates and may issue a partial refund (typically 10–40%) at our discretion.</li>
      <li><strong>Customer no-show:</strong> No refund. Vendor receives the full agreed amount.</li>
    </ul>

    <h2>4. Force Majeure</h2>
    <p>
      Cancellations due to force majeure (natural disasters, government-mandated lockdowns, communal violence, etc.) are eligible
      for a <strong>full refund or rescheduling</strong> at no cost, provided the request is made in writing within 7 days of the
      triggering event. BeeWeddy may waive its platform commission in such cases at its sole discretion.
    </p>

    <h2>5. Wallet Credits & Invite Bonuses</h2>
    <ul>
      <li>Promotional wallet credits (including ₹50 invite bonuses) are <strong>not refundable in cash</strong>.</li>
      <li>If a referred user signs up but does not transact within 90 days, the ₹50 bonus is automatically clawed back from the referrer's wallet.</li>
      <li>Withdrawals from the wallet are processed within 7 working days after admin approval; rejected withdrawals are credited back to the wallet immediately.</li>
    </ul>

    <h2>6. Platform Commission</h2>
    <p>
      The platform commission shown to customers (typically 8% / 10% / 12% on professionals / vendors / venues) is included in
      the displayed price. Where a refund is owed, the commission is refunded proportionally — i.e., a 50% booking refund returns
      50% of the commission.
    </p>

    <h2>7. Chargebacks & Disputes</h2>
    <p>
      Customers should always raise issues through BeeWeddy support before initiating a chargeback with their bank. Unjustified
      chargebacks may result in account suspension and forfeiture of wallet balance.
    </p>

    <h2>8. How to Claim a Refund</h2>
    <ol>
      <li>Open <em>Dashboard → My Bookings → [Booking] → Request Refund</em>, or email <a href={`mailto:${s.support_email}`} data-testid="refund-support-email">{s.support_email}</a>.</li>
      <li>Include booking ID, reason, and any supporting photos / videos.</li>
      <li>Our team responds within <strong>48 hours</strong> with a decision and timeline.</li>
      <li>Approved refunds reach your bank/UPI within 7 working days. Bank-side delays beyond our control may add 1–2 days.</li>
    </ol>

    <h2>9. Grievance Escalation</h2>
    <p>
      If you are not satisfied with our resolution, escalate to our Grievance Officer (see{' '}
      <a href="/privacy-policy">Privacy Policy §11</a>). Unresolved cases may be taken to consumer forums under the Consumer
      Protection Act, 2019. Jurisdiction: {s.jurisdiction_city}, Tamil Nadu.
    </p>

    <h2>10. Updates</h2>
    <p>
      This policy may be updated. The "Last updated" date at the top reflects the current version. The policy in effect at the
      time of your booking governs that booking, even if we update the policy later.
    </p>
  </LegalLayout>
  );
};

export default RefundPolicyPage;
